import type {HTMLAttributes, ReactNode} from 'react';

import {cn} from '../../../helpers/cn';
import {actionBarPaddingClassName} from '../../constants';

export type PageLayoutFooterPosition = 'fixed' | 'static';

export type PageLayoutProps = HTMLAttributes<HTMLDivElement> & {
  /** In-page header (tabs, filters, etc.) — shell header is separate. */
  header?: ReactNode;
  /** Scrollable main content. */
  children: ReactNode;
  /** Bottom action bar (usually a full-width button). */
  footer?: ReactNode;
  /**
   * `fixed` — bar sticks to viewport bottom (ticket, pin-list, cpc-list).
   * `static` — bar at flex column end (coupon/pro/extra-delivery lists).
   */
  footerPosition?: PageLayoutFooterPosition;
  /** Applied to the scrollable content wrapper. */
  contentClassName?: string;
  /** Id on the scrollable content wrapper (for InfiniteScroll `scrollableTarget`). */
  contentId?: string;
  footerClassName?: string;
};

export type PageLayoutFooterProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
};

const fixedFooterClassName =
  'fixed bottom-0 left-1/2 z-10 flex w-full max-w-mobile -translate-x-1/2 border-t border-gray-03 bg-white p-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))]';

const staticFooterClassName = 'flex h-20 shrink-0 border-t border-gray-03 bg-white px-4 py-4';

/** Wraps footer children so buttons stretch full width. */
export function PageLayoutFooter({children, className, ...props}: PageLayoutFooterProps) {
  return (
    <div className={cn('flex w-full [&>*]:w-full [&>*]:flex-1', className)} {...props}>
      {children}
    </div>
  );
}

/**
 * In-page layout: optional header + scrollable body + optional bottom action bar.
 * Use inside shell pages (`hasHeader: true`) where NavBar is provided by routing.
 */
export function PageLayout({
  header,
  children,
  footer,
  footerPosition = 'fixed',
  contentClassName,
  contentId,
  footerClassName,
  className,
  id,
  ...props
}: PageLayoutProps) {
  const hasFixedFooter = Boolean(footer) && footerPosition === 'fixed';
  const scrollContainerId = contentId ?? id;

  return (
    <div
      className={cn(
        'flex min-h-0 flex-1 flex-col overflow-hidden bg-white',
        hasFixedFooter && actionBarPaddingClassName,
        className
      )}
      {...props}
    >
      {header ? <div className="shrink-0 bg-white">{header}</div> : null}
      <div
        id={scrollContainerId}
        className={cn('flex min-h-0 flex-1 flex-col overflow-y-auto', contentClassName)}
      >
        {children}
      </div>
      {footer ? (
        <footer
          className={cn(
            footerPosition === 'fixed' ? fixedFooterClassName : staticFooterClassName,
            footerClassName
          )}
        >
          <PageLayoutFooter>{footer}</PageLayoutFooter>
        </footer>
      ) : null}
    </div>
  );
}

export type DetailPageLayoutProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
  footer?: ReactNode;
  contentClassName?: string;
  footerClassName?: string;
};

/**
 * Detail/form page: scrollable sections + static bottom button bar.
 * Replaces `calc(100dvh - Npx)` scroll areas in ticket/tracking details.
 */
export function DetailPageLayout({
  children,
  footer,
  contentClassName,
  footerClassName,
  className,
  ...props
}: DetailPageLayoutProps) {
  return (
    <div className={cn('flex min-h-0 flex-1 flex-col bg-white', className)} {...props}>
      <div className={cn('flex min-h-0 flex-1 flex-col overflow-y-auto', contentClassName)}>
        {children}
      </div>
      {footer ? (
        <footer
          className={cn(
            'flex h-[70px] shrink-0 border-t border-gray-03 bg-white p-2.5',
            footerClassName
          )}
        >
          <PageLayoutFooter>{footer}</PageLayoutFooter>
        </footer>
      ) : null}
    </div>
  );
}
